import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "@/context/AuthContext";
import getLink, { LINK_TYPE } from "@/lib/links";
import { getUrlParameter } from "@/utils/generic";
import { cosent } from "@/services/auth";
import { fireTagManagerEvent } from "@/services/tagmanager";
import { fireJuneEvent } from "../analytics/JuneAnalytics";
import { ArrowLeft } from "lucide-react";
import { useSignalEffect } from "@preact/signals-react";
import { SelectedHotel } from "./SelectHotelInput";
import { GoogleAuth } from "../auth/AuthComponents";

export function RegisterForm() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const { register, sendVerificationEmail } = useAuthContext();
  const isInvitation = !!getUrlParameter("email");
  const formSchema = z.object({
    email: z.string().email(t("auth.invalid_email")),
    password: z.string().min(6, t("auth.password_too_short")),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: getUrlParameter("email") ?? "",
      password: "",
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsLoading(true);
    try {
      const user = await register(values);

      fireJuneEvent("register", { email: values.email });

      await cosent({
        email: values.email,
        target: "dashboard-tos-privacy",
        consent: true,
      });

      await sendVerificationEmail({
        redirect: getLink(LINK_TYPE.VERIFY_EMAIL),
      });
    } catch (error: any) {
      console.log(error);

      const errorMessage = String(error.response.data.message).toLowerCase();

      if (errorMessage.includes("user already exists"))
        form.setError("email", {
          type: "manual",
          message: String(t("auth.email_already_taken")),
        });
    }
    setIsLoading(false);
  }

  useSignalEffect(() => {
    console.log("isInvitation", isInvitation);
    if (SelectedHotel.value.dest_id === "" && !isInvitation) {
      navigate(getLink(LINK_TYPE.REGISTER));
    } else {
      console.log("è un invito");
    }
  });

  return (
    <div className="mx-auto max-w-sm w-full">
      <Button
        className="md:hidden mb-8"
        variant={"default"}
        onClick={() => navigate(getLink(LINK_TYPE.REGISTER))}
      >
        <ArrowLeft className="mr-2 h-4 w-4" /> {t("auth.back")}
      </Button>
      <p className="text-2xl font-medium">{t("auth.register_title")}</p>
      <p className="text-base font-medium text-gray-500">
        {t("auth.register_subtitle")}
      </p>
      {!isInvitation && (
        <>
          <GoogleAuth mode="register" />
          <div className="border-b border-gray-200 mt-6"></div>
        </>
      )}
      <div>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="flex gap-2"></div>
            <div className="grid gap-2">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("auth.email")}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("auth.email")}
                        {...field}
                        disabled={isInvitation}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid gap-2">
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("auth.password_register")}</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder={t("auth.password_register")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-between items-center pt-8">
              <Button
                className="hidden md:inline-flex"
                variant={"default"}
                onClick={() => navigate(getLink(LINK_TYPE.REGISTER))}
              >
                <ArrowLeft className="mr-2 h-4 w-4" /> {t("auth.back")}
              </Button>
              <Button
                className="w-full md:w-auto"
                type="submit"
                variant={"takyon"}
                disabled={isLoading || !form.formState.isValid}
              >
                {isLoading ? t("auth.registering") : t("auth.next")}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}
